import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Panel, Btn, Verdict, StatusDot } from "@/components/kit";
import { FileText, AlertTriangle, Clapperboard } from "lucide-react";

export const WeeklyReport = () => {
    const qc = useQueryClient();
    const { data: reports = [] } = useQuery({
        queryKey: ["reports"],
        queryFn: async () => (await api.get("/reports")).data,
    });
    const report = reports[0];

    const generate = useMutation({
        mutationFn: async () => (await api.post("/reports/weekly")).data,
        onSuccess: () => {
            toast.success("Informe semanal generado por darkzOGx");
            qc.invalidateQueries({ queryKey: ["reports"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo generando el informe"),
    });

    return (
        <Panel
            title="Informe semanal · IA"
            testId="weekly-report-panel"
            className="mb-6"
            right={
                <Btn
                    testId="weekly-report-btn"
                    onClick={() => generate.mutate()}
                    disabled={generate.isPending}
                >
                    <span className="flex items-center gap-2">
                        <FileText size={13} />
                        {generate.isPending ? "Analizando…" : report ? "Regenerar" : "Generar informe"}
                    </span>
                </Btn>
            }
        >
            {generate.isPending && (
                <div className="mb-3 flex items-center gap-2 text-xs text-primary">
                    <StatusDot color="#00FFFF" pulse /> darkzOGx analizando canales, nichos y formatos (~20s)…
                </div>
            )}
            {!report && !generate.isPending && (
                <p className="text-sm text-muted-foreground">
                    Sin informes todavía. Genera el primero: la IA resume qué canales y nichos rinden
                    mejor y qué producir la próxima semana.
                </p>
            )}
            {report && (
                <div data-testid="weekly-report-content">
                    <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                        {report.period_start} → {report.period_end} · {report.videos_measured} videos ·{" "}
                        {report.brain}
                    </div>
                    <h3 className="font-head text-lg text-primary">{report.headline}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{report.summary}</p>

                    <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-3">
                        <div>
                            <div className="label mb-2">Canales</div>
                            <ul className="space-y-2">
                                {(report.top_channels || []).map((c, i) => (
                                    <li key={i} className="border border-border p-2.5 text-xs">
                                        <div className="flex items-center justify-between">
                                            <span>{c.name}</span>
                                            <Verdict v={c.verdict} />
                                        </div>
                                        <p className="mt-1 text-muted-foreground">{c.insight}</p>
                                    </li>
                                ))}
                            </ul>
                            <div className="label mb-2 mt-4">Nichos que rinden</div>
                            <ul className="space-y-1.5 text-xs text-muted-foreground">
                                {(report.top_niches || []).map((n, i) => (
                                    <li key={i}>
                                        <span className="text-primary">◢ {n.name}</span> — {n.reason}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <div className="label mb-2">Qué producir ahora</div>
                            <ul className="space-y-2">
                                {(report.recommendations || []).map((r, i) => (
                                    <li key={i} className="border border-border p-2.5 text-xs" data-testid={`report-rec-${i}`}>
                                        <div className="flex items-start gap-2">
                                            <Clapperboard size={13} className="mt-0.5 shrink-0 text-primary" />
                                            <div>
                                                <span className="text-foreground">{r.title}</span>
                                                <div className="mt-1 text-[10px] uppercase tracking-wider text-primary">
                                                    {r.format} · {r.channel}
                                                </div>
                                                <p className="mt-1 text-muted-foreground">{r.reason}</p>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <div className="label mb-2">Formato ganador</div>
                            <p className="border border-border p-2.5 text-xs text-muted-foreground">
                                {report.format_insight}
                            </p>
                            <div className="label mb-2 mt-4">Riesgos y alertas</div>
                            <ul className="space-y-1.5 text-xs">
                                {(report.risks || []).map((r, i) => (
                                    <li key={i} className="flex gap-2 text-muted-foreground">
                                        <AlertTriangle size={12} className="mt-0.5 shrink-0" style={{ color: "#FFD700" }} />
                                        {r}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </Panel>
    );
};
